import { ParentHandbookPage } from '@wonderschool/ccms-ui';
import { FileCategory } from '@wonderschool/common-base-types';
import { showToast, ToastTypeEnum, US_STATE_NAMES } from '@wonderschool/common-base-ui';
import { useUploadFile } from '@wonderschool/file-service-client';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { LoadingIndicator } from '../Components/Shared/BusyIndicator';
import { useContacts } from '../contacts';
import mimeTypes from '../helpers/mimeTypes';
import { useLocations } from '../hooks/useLocations';
import { useOrganization } from '../hooks/useOrganizations';
import { RouteNameEnum, useRoutes } from '../navigation';
import { logError } from '../rollbar';
import { fetchBillingSettings } from './BillingSettings/api';
import type { LateFeePolicy } from './BillingSettings/types';

const ParentHandbook = () => {
  const { gotoRouteByName, getActiveRoute, getRoutePath } = useRoutes();
  const { t } = useTranslation();
  const params = useParams<{ step: string }>();

  const { uploadFile, reset: resetUploadHook } = useUploadFile();
  const { id: currentOrganizationId, name: programName, createdBy } = useOrganization();
  const { list: locations } = useLocations();
  const { staffContacts } = useContacts();

  const director = staffContacts?.find((staff) => staff.uid === createdBy);
  const directorEmail = director?.email;
  const directorName = director?.displayName;

  const stateAbbreviation = locations?.[0]?.state || '';

  // Billing Settings owns the late payment fee (PAYM-5202). `undefined` means the
  // read is still in flight; `null` means it failed.
  const [lateFeePolicy, setLateFeePolicy] = useState<LateFeePolicy | null | undefined>(undefined);

  useEffect(() => {
    // Wait for the org to land rather than reading defaults under an empty id —
    // a second resolve would re-reset a form the provider may already be filling in.
    if (!currentOrganizationId) return;

    // Drop the previous org's policy before fetching the new one, so the loading
    // gate below re-engages on every org change rather than only the first.
    // The org can switch while this page stays mounted — Impersonation renders as
    // a layout sibling of the routed page, so "Stop Impersonating" never unmounts
    // us. Without this, the old org's terms would render, and could be printed
    // into the new org's signed handbook, until the new fetch resolved.
    setLateFeePolicy(undefined);

    let cancelled = false;
    fetchBillingSettings(currentOrganizationId)
      .then(({ lateFeePolicy: policy }) => {
        if (!cancelled) setLateFeePolicy(policy);
      })
      .catch((error) => {
        logError('Error loading billing settings for parent handbook', {
          clientError: error,
        });
        // There is no longer a fallback: ws-common requires the policy and the
        // handbook renders it into a PDF families sign. Rendering the wizard
        // without it would print platform defaults as if they were this
        // provider's terms, so fail closed instead.
        if (!cancelled) setLateFeePolicy(null);
      });

    return () => {
      cancelled = true;
    };
  }, [currentOrganizationId]);

  // Only link to Billing Settings when this user can actually reach it —
  // getActiveRoute applies the route's `can_list_billing` permission and
  // FINANCES flag. Without an href ws-common renders support copy instead.
  const billingSettingsRoute = getActiveRoute(RouteNameEnum.BILLING_SETTINGS);
  const settingsHref = billingSettingsRoute ? getRoutePath(billingSettingsRoute) : undefined;

  // Memoized because ParentHandbookPage keys a `form.reset()` effect on this
  // prop's identity — a fresh object per render would wipe the form as you type.
  const latePaymentPolicy = useMemo(() => {
    if (!lateFeePolicy) return undefined;
    // Non-null from here: the render gate below gives up before this is used.
    const { policyEnabled, lateFeeType, lateFeeAmount, gracePeriodDays } = lateFeePolicy;
    return { policyEnabled, lateFeeType, lateFeeAmount, gracePeriodDays, ...(settingsHref ? { settingsHref } : {}) };
  }, [lateFeePolicy, settingsHref]);

  const handleRouteChangeAfterUpload = () => {
    gotoRouteByName(RouteNameEnum.DOCUMENTS, [{ name: 'showParentHandbookConfirmation', value: 'true' }]);
  };

  const handleCancellation = () => {
    gotoRouteByName(RouteNameEnum.DOCUMENTS, [{ name: 'showParentHandbookConfirmation', value: 'false' }]);
  };

  const schoolInfo = useMemo(
    () => ({
      name: programName,
      directorEmail,
      directorName,
      state: US_STATE_NAMES?.[stateAbbreviation] || stateAbbreviation,
    }),
    [programName, stateAbbreviation, directorEmail, directorName]
  );

  const handleFinish = async (pdfBlob: Blob) => {
    try {
      await uploadFile({
        file: new File([pdfBlob], `${programName}-parent handbook.pdf`, { type: mimeTypes.PDF }),
        options: {
          fileAction: 'downloadable',
          fileCategory: FileCategory.PARENT_HANDBOOK,
          metadata: {
            uploadPath: `/organizations/${currentOrganizationId}`,
          },
        },
      });
      resetUploadHook();
      handleRouteChangeAfterUpload();
    } catch (error) {
      logError('Error uploading parent handbook', {
        clientError: error,
      });
    }
  };

  const handleError = (error: Error) => {
    handleCancellation();
    showToast(ToastTypeEnum.Error, t('parentHandbook.updateError'));
    logError('Error updating parent handbook', {
      clientError: error,
    });
  };

  // Render nothing but the spinner until the policy settles, for the same
  // reason the prop is memoized: a late arrival resets the wizard's form.
  if (lateFeePolicy === undefined) return <LoadingIndicator />;

  // The read failed. Better to send the provider back than to generate a
  // handbook stating late payment terms we could not confirm.
  if (!latePaymentPolicy) {
    showToast(ToastTypeEnum.Error, t('parentHandbook.latePaymentPolicyError'));
    handleCancellation();
    return null;
  }

  return (
    <ParentHandbookPage
      useParams={() => ({ step: params.step || '' })}
      onClose={handleCancellation}
      onFinish={handleFinish}
      onError={handleError}
      schoolInformation={schoolInfo}
      latePaymentPolicy={latePaymentPolicy}
    />
  );
};

export default ParentHandbook;
